<template>
  <v-container fluid class="main-container">
    <div class="campaign-list-page">
      <section-header
        eyebrow="Work"
        title="Stores"
        :lede="storesLede"
      >
        <template #actions>
          <v-btn
            class="add-campaign-button"
            @click="addCampaign()"
            data-testid="cl-new-campaign"
            color="primary"
            :disabled="interfaceLocked"
            rounded="pill"
          >
            <v-icon start color="white" icon="add" />
            <span class="text-white">New Store</span>
          </v-btn>
        </template>
      </section-header>

      <div class="campaign-filter-bar" data-testid="campaign-filter-bar">
        <div class="campaign-filter-bar__status-pills">
          <span
            v-for="opt in statusFilterOptions"
            :key="opt.value"
            class="campaign-filter-pill"
            :class="{ 'campaign-filter-pill--active': statusFilter === opt.value }"
            :data-testid="`cl-status-filter-${opt.value}`"
            role="button"
            tabindex="0"
            :aria-pressed="statusFilter === opt.value ? 'true' : 'false'"
            @click="statusFilter = opt.value"
            @keydown.enter="statusFilter = opt.value"
            @keydown.space.prevent="statusFilter = opt.value"
          >{{ opt.label }}</span>
        </div>
        <!--
          Filter counts are scoped to the loaded page. campaigns[] is
          server-paginated (LIMIT/OFFSET); the status filter is a
          client-side narrow over the current page. Calling out "on this
          page" keeps the framing honest when a distributor has more
          stores than fit in one page.
        -->
        <div class="campaign-filter-bar__status" data-testid="campaign-status">
          <span v-if="storesPending">Loading stores...</span>
          <span v-else>
            {{ visibleCampaigns.length }} of {{ campaigns.length }} on this page
          </span>
          <span v-if="loadError" class="campaign-status-error"> · {{ loadError }}</span>
        </div>
      </div>

      <!--
        Non-owners on a suspended/canceled tenant: the list is locked and
        renew is Owner-only, so say why and who can fix it. No action here.
      -->
      <v-alert
        v-if="ownerContactNotice"
        type="warning"
        variant="tonal"
        class="mb-4"
        data-testid="cl-subscription-owner-notice"
      >{{ ownerContactNotice }}</v-alert>

      <v-alert
        v-if="ownerChangedNotice"
        type="success"
        variant="tonal"
        density="compact"
        closable
        class="mb-3"
        data-testid="store-owner-success"
        @click:close="ownerChangedNotice = ''"
      >{{ ownerChangedNotice }}</v-alert>

      <campaign-list-table
        :campaigns="visibleCampaigns"
        :campaign-count="campaignCount"
        :loading="storesPending"
        :show-sales-rep="showSalesRep"
        :campaign-switch-disabled="campaignSwitchDisabled"
        :interface-locked="interfaceLocked"
        :subscription-status="subscriptionStatus"
        :pagination="pagination"
        :complete-button-key="completeButtonKey"
        :stock-button-key="stockButtonKey"
        :can-change-owner="canChangeStoreOwner"
        :empty-message="emptyMessage"
        @open-campaign="openCampaign"
        @open-batches="openBatches"
        @active-campaign="activeCampaign"
        @duplicate-campaign="duplicateCampaign"
        @delete-campaign="openDeleteDialog"
        @archive-campaign="openArchiveDialog"
        @unarchive-campaign="unarchiveCampaign"
        @change-owner="openChangeOwner"
        @change-completeness="changeCompletenessHandler"
        @check-low-stock="checkLowStock"
        @filter-changed="onFilterChanged"
        @update:page="pagination.page = $event"
        @update:rows-per-page="onRowsPerPageChange"
      />
    </div>

    <!-- Owner-only. Also opened by /distributor/stores?changeOwner=<id>, the
         link the Team page shows for each store a deactivation reassigned. -->
    <change-store-owner-dialog
      v-if="canChangeStoreOwner && changeOwnerStore"
      :key="changeOwnerStore.id"
      v-model="changeOwnerOpen"
      :store-id="changeOwnerStore.id"
      :store-name="changeOwnerStore.name"
      :current-owner-id="changeOwnerStore.created_by_id"
      @changed="onStoreOwnerChanged"
    />

    <campaign-duplicate-dialog
      v-if="duplicateCampaignDialogOpened"
      :campaignId="duplicateCampaignId"
      @duplicate-campaign="duplicateCampaign"
      @close-modal="closeDuplicateDialog"
    />

    <campaign-complete-dialog
      v-if="completeCampaignDialogOpened"
      :campaign="completeCampaignData"
      :modalPopped="completeCampaignDialogOpened"
      @complete-campaign="completeCampaign"
      @close-modal="closeCompleteDialog"
    />

    <create-batch-dialog
      ref="createBatchDialog"
      completeStore
      @createBatchAndCompleteStore="processOrdersAndCompleteStore"
    />

    <v-dialog v-model="uncompleteCampaignDialogOpened" max-width="800">
      <v-card>
        <v-card-title class="text-h5 bg-error text-white"
          >Unlock completed store?</v-card-title
        >

        <v-card-text>
          <div class="text-h6" style="line-height: 1.4 !important">
            <p class="mt-2 mb-3">
              Do you want to unlock completed store
              <strong
                >"{{
                  uncompleteCampaignData ? uncompleteCampaignData.name : ""
                }}"?</strong
              >
            </p>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-3 px-3">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            color="error"
            @click="closeUncompleteDialog()"
            data-testid="cl-complete-unlock-cancel"
            >Cancel</v-btn
          >
          <v-btn
            :loading="uncompleteCampaignPending"
            :disabled="uncompleteCampaignPending"
            color="error"
            @click="uncompleteCampaign(uncompleteCampaignData)"
            data-testid="cl-complete-unlock-confirm"
          >
            Unlock
            <template v-slot:loader>
              <span>Saving...</span>
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteCampaignDialogOpened" max-width="800">
      <v-card>
        <v-card-title class="text-h5 bg-error text-white"
          >Delete store</v-card-title
        >

        <v-card-text>
          <div class="text-h6" style="line-height: 1.4 !important">
            <p class="mt-2 mb-3">
              Do you really want to delete store
              <strong
                >"{{
                  deleteCampaignData ? deleteCampaignData.name : ""
                }}"?</strong
              >
              This operation can't be undone.
            </p>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-3 px-3">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            color="error"
            @click="closeDeleteDialog()"
            data-testid="cl-delete-cancel"
            >cancel</v-btn
          >
          <v-btn
            :loading="deleteCampaignPending"
            :disabled="deleteCampaignPending"
            color="error"
            @click="deleteCampaign(deleteCampaignData)"
            data-testid="cl-delete-confirm"
          >
            Delete
            <template v-slot:loader>
              <span>Saving...</span>
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Archive: hides the store from this list, the editor and the
         storefront, and switches it off; its orders stay visible. The
         server refuses (409) while an order is still open, and its reason
         is shown here so the distributor knows what to finish first. -->
    <v-dialog v-model="archiveDialogOpened" max-width="800">
      <v-card>
        <v-card-title class="text-h5 bg-primary text-white"
          >Archive store</v-card-title
        >

        <v-card-text>
          <div class="text-h6" style="line-height: 1.4 !important">
            <p class="mt-2 mb-3">
              Archive store
              <strong>"{{ archiveData ? archiveData.name : "" }}"?</strong>
              Shoppers can no longer reach it, and it moves to the Archived
              list. Its orders stay available in Orders and Reports. You can
              unarchive it later; it comes back closed.
            </p>
          </div>
          <v-alert
            v-if="archiveError"
            type="warning"
            variant="tonal"
            density="compact"
            data-testid="cl-archive-error"
          >{{ archiveError }}</v-alert>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-3 px-3">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            color="primary"
            @click="closeArchiveDialog()"
            data-testid="cl-archive-cancel"
            >cancel</v-btn
          >
          <v-btn
            :loading="archivePending"
            :disabled="archivePending"
            color="primary"
            @click="archiveCampaign()"
            data-testid="cl-archive-confirm"
          >
            Archive
            <template v-slot:loader>
              <span>Saving...</span>
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <campaign-credential-dialogs
      :duplicateDialogOpen="duplicateCredentialDialog"
      :changeDialogOpen="changeCredentialDialog"
      :activatedCampaign="activatedCampaign"
      :selectedStore="selectedStore"
      :createdByAnotherSalesRep="createdByAnotherSalesRep"
      :disableDeactiveCampaignBy="disableDeactiveCampaignBy"
      :displayChangeCredentialField="displayChangeCredentialField"
      :displayChangeURLField="displayChangeURLField"
      :shopperLogin="newShopperLogin"
      :shopperPassword="newShopperPassword"
      :shopperCustomLink="newShopperCustomLink"
      :activatedCampaignShopperUsername="activatedCampaignShopperUsername"
      :activatedCampaignShopperURL="activatedCampaignShopperURL"
      @update:duplicateDialogOpen="duplicateCredentialDialog = $event"
      @update:changeDialogOpen="changeCredentialDialog = $event"
      @update:validForm="validCredentialForm = $event"
      @deactivate-campaign="deactivateCampaign"
      @open-credentials="openCredentialsDialog"
      @change-credentials="changeCredentials"
    />

    <!--
      The "payment method" link MUST land on the page that actually holds
      payment-method controls. It used to point at `/distributor/profile`,
      which was the company-profile page; that path now mounts the PERSONAL
      profile page (name / username / email / password) and has no payment
      UI at all -- so the one link telling a SUSPENDED distributor how to
      restore access dead-ended and they could not pay. Payments live at
      `/distributor/account/payments` (router/index.js).
    -->
    <v-dialog v-model="showSuspendAlert" max-width="800">
      <v-card>
        <v-card-text>
          <span class="text-h6">
            Your account has been suspended. Please enter a valid
            <a
              :href="baseUrl + '/distributor/account/payments'"
              class="link"
              >payment method</a
            >
            to resume access to your account.
          </span>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="flat"
            color="error"
            @click="showSuspendAlert = false"
            data-testid="cl-deactivate-campaign"
            >Okay
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <stock-availability-dialog
      ref="stock-availability-dialog"
      :stockInfo="stockInfoList"
    />
    <floating-message
      v-if="canRenewSubscription"
      @click="openRenewSubscriptionDialog"
      :buttonText="subscriptionButtonText"
      :message="subscriptionMessage"
      :showDialog="
        subscriptionStatus == 'suspended' || subscriptionStatus == 'canceled'
      "
    />
    <renew-subscription-dialog
      v-if="canRenewSubscription"
      ref="renewSubscriptionDialog"
      :subscription="subscription"
      :distributor_id="distributorID"
      @renew-subscription="getSubscriptionStatus"
    />
  </v-container>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import { reopenedCloseDate } from "./storeWindow.js";
import rules from "@/mixins/rules";
import CampaignCompleteDialog from "./StoreCompleteDialog";
import CampaignDuplicateDialog from "./StoreDuplicateDialog";
import CampaignListTable from "./StoreListTable.vue";
import CampaignCredentialDialogs from "./StoreCredentialDialogs.vue";
import ChangeStoreOwnerDialog from "./ChangeStoreOwnerDialog.vue";

import FloatingMessage from "@/components/FloatingMessage.vue";
import StockAvailabilityDialog from "./StoreProducts/StockAvailabilityDialog.vue";
import RenewSubscriptionDialog from "@/pages/distributor/account/RenewSubscriptionDialog.vue";
import CreateBatchDialog from "./CreateBatchDialog.vue";
import SectionHeader from "@/components/distributor/SectionHeader.vue";
import { useAccountStore } from "@/stores/account";
import { usePermissions } from "@/composables/usePermissions";
import { deriveStoreStatus } from "./storeStatus";
import { preserveOrder } from "@/utils/preserveOrder";
import { ROLE_SLUGS } from "./storeAccountRoles.js";
import {
  notifyStoreArchiveChanged,
  notifyStoreDeleted,
} from "@/stores/sidebarRefresh";

export default {
  // Impersonation fix (2026-06-07, chain item 3 of 3, audit finding #7):
  // mounted() previously gated subscription loading on
  // `role.name == "Owner"` and built the URL from
  // `state.account.distributor_id`. Under SA-impersonation, role.name stays
  // "Super Admin" so the branch never fired -- and even if it had, the
  // SA's own distributor_id is null. Switched to the Pinia *effective*
  // tenant (`accountStore.distributor?.id`): Owner-own-tenant and
  // SA-impersonating both work; true SA (no effective tenant) skips the
  // load. `showSalesRep` is intentionally left alone -- the existing
  // `Super Admin` branch already covers SA-impersonating by coincidence,
  // and changing it would risk scope creep.
  setup() {
    const accountStore = useAccountStore();
    const { can } = usePermissions();
    return { accountStore, can };
  },

  mixins: [rules],

  components: {
    "campaign-complete-dialog": CampaignCompleteDialog,
    "campaign-duplicate-dialog": CampaignDuplicateDialog,
    "campaign-list-table": CampaignListTable,
    "campaign-credential-dialogs": CampaignCredentialDialogs,
    "change-store-owner-dialog": ChangeStoreOwnerDialog,
    "floating-message": FloatingMessage,
    "stock-availability-dialog": StockAvailabilityDialog,
    "renew-subscription-dialog": RenewSubscriptionDialog,
    "create-batch-dialog": CreateBatchDialog,
    "section-header": SectionHeader,
  },

  data() {
    return {
      campaigns: [],
      storesPending: true,
      // Change owner dialog: the store being moved ({ id, name,
      // created_by_id }) and the confirmation shown after a move.
      changeOwnerStore: null,
      changeOwnerOpen: false,
      ownerChangedNotice: "",
      statusFilter: "all",
      completeDialogCampaign: null,

      completeCampaignDialogOpened: false,
      completeCampaignPending: false,
      completeCampaignData: null,

      uncompleteCampaignDialogOpened: false,
      uncompleteCampaignPending: false,
      uncompleteCampaignData: null,

      deleteCampaignDialogOpened: false,
      deleteCampaignPending: false,
      deleteCampaignData: null,

      archiveDialogOpened: false,
      archivePending: false,
      archiveData: null,
      archiveError: "",

      duplicateCampaignDialogOpened: false,
      duplicateCampaignId: null,

      campaignCount: 0,
      pagination: {
        sortBy: "modified_at",
        descending: true,
        page: 1,
        rowsPerPage: 50,
      },
      filters: [],

      duplicateCredentialDialog: false,
      activatedCampaign: null,

      changeCredentialDialog: false,
      selectedStore: null,

      newShopperLogin: "",
      newShopperPassword: "",
      newShopperCustomLink: "",
      validCredentialForm: false,

      disableDeactiveCampaignBy: "",
      possibleRoles: null,

      createdByAnotherSalesRep: false,
      showCredentialDialogDirectly: false,

      activatedCampaignShopperUsername: "",
      activatedCampaignShopperURL: "",
      subscriptionStatus: null,
      showSuspendAlert: false,

      pressingToolbarButton: false,

      stockInfoList: null,

      stockButtonKey: 0,
      completeButtonKey: 0,

      subscription: null,

      distributorID: null,

      displayChangeURLField: false,
      displayChangeCredentialField: false,
      loadError: null,
    };
  },

  computed: {
    baseUrl() {
      return process.env.BASE_URL || window.location.origin;
    },

    statusFilterOptions() {
      return [
        { value: "all", label: "All" },
        { value: "live", label: "Live" },
        { value: "draft", label: "Draft" },
        { value: "closed", label: "Closed" },
        // Not a client-side narrow like the others: archived stores are
        // left out of the default list by the server, so this view asks
        // the server for them (loadStores). It is how a distributor finds
        // a store to unarchive.
        { value: "archived", label: "Archived" },
      ];
    },

    archivedView() {
      return this.statusFilter === "archived";
    },

    emptyMessage() {
      return this.archivedView
        ? "No archived stores."
        : "You haven't built any stores yet.";
    },

    visibleCampaigns() {
      if (this.statusFilter === "all" || this.archivedView) return this.campaigns;
      return this.campaigns.filter(
        (c) => deriveStoreStatus(c) === this.statusFilter
      );
    },

    storesLede() {
      if (this.storesPending) return "Loading...";
      if (this.archivedView) {
        const n = this.campaignCount;
        return `Archived stores are hidden from shoppers and from your store list. ${n} archived.`;
      }
      // total comes from the server count (campaignCount); live is derived
      // from the loaded page (campaigns). When the distributor has more
      // stores than the page size, live is "live on this page" rather than
      // a true global count -- we frame the page-scoped half explicitly so
      // the framing matches the data. Wiring a real "live count" would
      // require a backend aggregate; deferred.
      const total = this.campaignCount;
      const liveOnPage = this.campaigns.filter(
        (c) => deriveStoreStatus(c) === "live"
      ).length;
      const noun = total === 1 ? "store" : "stores";
      const livePartial = this.campaigns.length < total
        ? `${liveOnPage} live on this page`
        : `${liveOnPage} live`;
      return `Every store across your distributor. ${total} ${noun} · ${livePartial}.`;
    },

    // Owner-only (Jake, 2026-09-30): the API gates the team subscription on
    // distributor.account.read and renew on
    // distributor.danger_zone.cancel_account. Builders and Sales Executives
    // use this page too; they get neither the request nor the renew action.
    canReadTeamSubscription() {
      return this.can("distributor.account.read");
    },

    canRenewSubscription() {
      return this.can("distributor.danger_zone.cancel_account");
    },

    // Owner-only, the same capability the change-owner route requires.
    canChangeStoreOwner() {
      return this.can("distributor.team.invite");
    },

    // Shown instead of the renew banner to accounts that cannot renew. The
    // state comes from getSubscriptionStatus, which any tenant account may
    // read, never from the Owner-only endpoints.
    ownerContactNotice() {
      if (!this.interfaceLocked || this.canRenewSubscription) return null;
      const state = this.subscriptionStatus === "suspended" ? "suspended" : "canceled";
      return `This account is ${state}. Contact your account Owner to reactivate it.`;
    },

    interfaceLocked() {
      return (
        this.subscriptionStatus == "suspended" ||
        this.subscriptionStatus == "canceled"
      );
    },

    subscriptionMessage() {
      if (this.subscriptionStatus == "suspended") {
        return "There is a problem with your payment method. Your subscription has been suspended. Click here to update your payment info";
      } else if (this.subscriptionStatus == "canceled") {
        return "Your subscription is canceled and this account is read-only. Click here to renew";
      }
    },

    subscriptionButtonText() {
      if (this.subscriptionStatus == "suspended") {
        return "Update Payment Info";
      } else if (this.subscriptionStatus == "canceled") {
        return "Renew Susbcription";
      }
    },

    campaignSwitchDisabled() {
      let disabled = false;

      if (
        this.$store.state.account &&
        this.$store.state.account.role.name == "Client Admin"
      ) {
        disabled = true;
      }

      return disabled;
    },

    showSalesRep() {
      let res = false;
      if (
        this.$store.state.account &&
        (this.$store.state.account.role.name == "Owner" ||
          this.$store.state.account.role.name == "Super Admin")
      ) {
        res = true;
      }
      return res;
    },
  },

  watch: {
    changeOwnerOpen(open) {
      if (!open) this.closeChangeOwner();
    },
    pagination: {
      handler() {
        this.loadStores();
      },
      deep: true,
    },
    // The Archived view is a different server query, so crossing into or
    // out of it reloads from the first page.
    statusFilter(next, prev) {
      if ((next === "archived") === (prev === "archived")) return;
      if (this.pagination.page !== 1) {
        this.pagination.page = 1; // the pagination watcher reloads
      } else {
        this.loadStores();
      }
    },
  },

  async mounted() {
    await this.loadStores();
    this.openChangeOwnerFromQuery();
    this.$store.state.title = "Create A Store";
    this.$store.state.topTitle = false;
    try {
      await this.$store.dispatch("jv/get", "roles");
    } catch (err) {
      this.$store.dispatch('raiseError', err);
    }
    await this.getSubscriptionStatus();
    // Use the Pinia *effective* tenant (`accountStore.distributor.id`) so
    // SA-impersonating-ACME loads ACME's subscription. Role-based gate
    // dropped the impersonation case. See setup() comment above.
    // getTeamSubscriptionData is Owner-only (see canReadTeamSubscription);
    // Builders, Sales Executives and Client Admins reach this page too.
    const piniaDistId = this.accountStore?.distributor?.id;
    if (piniaDistId != null && this.canReadTeamSubscription) {
      this.distributorID = piniaDistId;
      await this.loadSubscription();
    }
  },

  unmounted() {
    this.$store.state.title = "";
    this.$store.state.topTitle = false;
  },

  methods: {
    openChangeOwner(item) {
      this.changeOwnerStore = {
        id: item.id,
        name: item.name,
        created_by_id: item.created_by_id ?? null,
      };
      this.changeOwnerOpen = true;
    },

    // ?changeOwner=<id> deep link from the Team page's deactivation results.
    // The store may not be on the loaded page, so fall back to its id.
    openChangeOwnerFromQuery() {
      const raw = this.$route?.query?.changeOwner;
      const id = Number.parseInt(raw, 10);
      if (!this.canChangeStoreOwner || !Number.isInteger(id)) return;
      const row = this.campaigns.find((c) => c.id === id);
      this.openChangeOwner(row || { id, name: "", created_by_id: null });
    },

    // The dialog closed (saved or cancelled): forget the store and drop the
    // deep link, so a refresh does not reopen it.
    closeChangeOwner() {
      this.changeOwnerStore = null;
      if (this.$route?.query?.changeOwner !== undefined) {
        const { changeOwner, ...query } = this.$route.query;
        this.$router.replace({ path: this.$route.path, query });
      }
    },

    async onStoreOwnerChanged(result) {
      this.ownerChangedNotice = `${result.name} now belongs to ${result.owner_name}.`;
      await this.loadStores();
    },

    onRowsPerPageChange(val) {
      this.pagination.rowsPerPage = val;
      this.pagination.page = 1;
    },

    onFilterChanged(filters) {
      this.filters = filters;
      this.loadStores();
    },

    async getSubscriptionStatus() {
      try {
        const response = await this.$axios.get(`/subscription/getSubscriptionStatus`);
        this.subscriptionStatus = response.data;
      } catch (err) {
        this.$store.dispatch('raiseError', err);
        this.subscriptionStatus = null;
      }
    },

    openRenewSubscriptionDialog() {
      this.$refs["renewSubscriptionDialog"].show();
    },

    async loadSubscription() {
      try {
        // Use this.distributorID (Pinia-sourced) instead of
        // state.account.distributor_id, which is null for SA-impersonating.
        const response = await this.$axios.get(
          `/subscription/getTeamSubscriptionData/${this.distributorID}`
        );
        this.subscription = response.data;
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      }

      if (this.subscription && this.subscription["status"] == "suspended") {
        this.showSuspendAlert = true;
      } else {
        this.showSuspendAlert = false;
      }
    },

    async checkLowStock(item) {
      item.loading = true;
      this.stockButtonKey++;
      let result;
      try {
        result = await this.$axios.post("/campaign/getCampaignStockInfo/", {
          campaign_id: item.id,
        });
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      } finally {
        item.loading = false;
        this.stockButtonKey++;
        if (result && result.data) {
          this.stockInfoList = result.data;
          this.$refs["stock-availability-dialog"].show();
        }
      }
    },

    openCampaign(item) {
      if (!this.pressingToolbarButton && !item.loading) {
        if (item.id) {
          // Push directly to the canonical nested editor URL; the
          // legacy /distributor/campaigns/:id path was removed (2026-10-21).
          const targetRoute = "/distributor/stores/" + item.id + "/edit";
          if (this.$route.path !== targetRoute) {
            this.$router.push(targetRoute);
          }
        }
      }
    },

    // An archived store's only way into the editor: its Batches section
    // (the restricted mode). Every other section answers Access Denied.
    openBatches(item) {
      if (item && item.id) {
        this.$router.push(`/distributor/stores/${item.id}/edit/batches`);
      }
    },

    formatFilters() {
      let filterValues = {};
      for (const key in this.filters) {
        if (Object.hasOwnProperty.call(this.filters, key)) {
          const filterValue = this.filters[key];
          let newKey = key.substring(key.indexOf(".") + 1, key.length);
          filterValues[newKey] = this.filters[key];
        }
      }
      return filterValues;
    },

    addCampaign() {
      // Canonical add-store URL; the legacy /distributor/campaigns/add
      // path was removed (2026-10-21).
      this.$router.push("/distributor/stores/add");
      this.$store.commit("setDistributorStatus", false);
      this.$store.commit("setActionButtonsStatus", true);
      this.$store.commit("setDisableNextButton", false);
      this.$store.commit("setNewOrderAdded", false);
      this.$store.commit("setBFLCostChanged", false);
      this.$store.commit("setIsClickedExitButton", false);
    },

    async loadStores({ preserveOrder: preserveOrderOpt = false } = {}) {
      this.$store.commit("setEditStoreName", "");
      this.storesPending = true;
      this.loadError = null;
      try {
        const archived = this.archivedView;
        let result = await this.$axios.get(
          archived
            ? "/campaign/getCampaignCount?archived=true"
            : "/campaign/getCampaignCount"
        );
        this.campaignCount = result.data.count;
        const { sortBy, descending, page, rowsPerPage } = this.pagination;
        let orderBy = "";
        if (sortBy) {
          if (descending) {
            orderBy = " order by " + sortBy + " desc";
          } else {
            orderBy = " order by " + sortBy;
          }
          // sort by Sales Reps
          if (sortBy == "sales_reps") {
            if (descending) {
              orderBy =
                " order by role_id desc, first_name desc, last_name desc";
            } else {
              orderBy = " order by role_id, first_name, last_name";
            }
          }
        } else {
          orderBy = " order by id";
        }
        let formattedFilters =
          this.formatFilters() != {} ? this.formatFilters() : null;
        let limit =
          " limit " + rowsPerPage + " offset " + (page - 1) * rowsPerPage;

        result = await this.$axios.post("/campaign/getCampaignList", {
          order_by: orderBy,
          limit: limit,
          filters: formattedFilters,
          archived,
        });

        result.data.forEach((element) => {
          if (element["complete"]) {
            element["status"] = "Complete";
          } else if (!this.campaignSwitchDisabled && element["is_ready"]) {
            element["status"] = element["active"];
          } else {
            element["status"] = "Incomplete";
          }
        });
        const fresh = Object.values(result.data);
        // After a row-level mutation (e.g., toggling Open/Closed), the
        // server's modified_at desc sort would move the just-touched
        // row to position 0 and shift every other row down -- visually
        // appearing as if every switch reversed. preserveOrder=true
        // re-applies the prior row order to the fresh data so only the
        // toggled row's cell state changes; other rows stay put.
        // Server-side new rows append at the end; deletions drop out.
        if (preserveOrderOpt && this.campaigns.length > 0) {
          this.campaigns = preserveOrder(this.campaigns, fresh);
        } else {
          this.campaigns = fresh;
        }
        this.storesPending = false;
      } catch (err) {
        this.$store.dispatch('raiseError', err);
        this.campaigns = [];
        this.loadError = "Could not load stores";
      } finally {
        this.storesPending = false;
      }
    },

    async duplicateCampaign(campaignId, isTransfer = false) {
      this.pressingToolbarButton = true;
      this.storesPending = true;
      try {
        await this.$axios.post(
          `/stores/${campaignId.toString()}/duplicate`,
          {
            isTransfer: true,
          }
        );
        this.closeDuplicateDialog();
        await this.loadStores();
      } finally {
        this.storesPending = false;
      }
    },

    openDuplicateDialog(campaignId) {
      this.duplicateCampaignId = campaignId;
      this.duplicateCampaignDialogOpened = true;
    },

    closeDuplicateDialog() {
      this.duplicateCampaignId = null;
      this.duplicateCampaignDialogOpened = false;
    },

    openDeleteDialog(campaign) {
      this.pressingToolbarButton = true;
      this.deleteCampaignData = campaign;
      this.deleteCampaignDialogOpened = true;
    },

    closeDeleteDialog() {
      this.deleteCampaignData = null;
      this.deleteCampaignDialogOpened = false;
    },

    async deleteCampaign(campaign) {
      this.pressingToolbarButton = true;
      this.deleteCampaignPending = true;
      try {
        let result = await this.$axios.get(
          `/campaign/deleteCampaign/${campaign.id}`
        );
        this.closeDeleteDialog();
        this.loadStores();
        // Deleting a store drops a row -> the sidebar STORES count changed;
        // announce it so the distributor sidebar refetches. Success-only: the
        // GET above has no catch, so a failed delete throws out before here
        // (no row removed). Generic delegated seam (trello.com/c/g9K52FsA).
        notifyStoreDeleted();
      } finally {
        this.deleteCampaignPending = false;
      }
    },

    openArchiveDialog(campaign) {
      this.archiveData = campaign;
      this.archiveError = "";
      this.archiveDialogOpened = true;
    },

    closeArchiveDialog() {
      this.archiveData = null;
      this.archiveError = "";
      this.archiveDialogOpened = false;
    },

    async archiveCampaign() {
      const campaign = this.archiveData;
      if (!campaign) return;
      this.archivePending = true;
      this.archiveError = "";
      try {
        await this.$axios.post(`/stores/${campaign.id}/archive`);
      } catch (err) {
        // 409: the store still has open orders. Keep the dialog open and
        // show the server's own sentence; anything else is a real error.
        const data = err && err.response && err.response.data;
        if (err && err.response && err.response.status === 409 && data && data.message) {
          this.archiveError = data.message;
        } else {
          this.$store.dispatch("raiseError", err);
        }
        return;
      } finally {
        this.archivePending = false;
      }
      this.closeArchiveDialog();
      notifyStoreArchiveChanged();
      await this.loadStores();
    },

    async unarchiveCampaign(campaign) {
      try {
        await this.$axios.post(`/stores/${campaign.id}/unarchive`);
      } catch (err) {
        this.$store.dispatch("raiseError", err);
        return;
      }
      notifyStoreArchiveChanged();
      await this.loadStores();
    },

    async activeCampaign(campaign) {
      if (campaign.active) {
        // Readiness pre-flight: GET /api/stores/<id>/readiness/ BEFORE the
        // flip. Its ``launch`` block is THE server answer to "can this store
        // go live now?" (state_transition_guard
        // .compute_store_launch_eligibility) -- the same answer the Review &
        // Launch button reads, so the toggle and the button cannot disagree.
        // The backend guard on the flip stays the authority; this lets us
        // show the server's reasons in an alert without surfacing a 422.
        try {
          const readinessStore = (
            await import("@/stores/storeReadiness")
          ).useStoreReadinessStore();
          const readiness = await readinessStore.fetchReadiness(campaign.id);
          const launch = readiness && readiness.launch;
          if (launch && launch.live !== true && launch.can_launch === false) {
            campaign.active = false;
            // Surface the server's own reason messages directly. Each
            // already carries a human-readable message scoped to its actual
            // cause (artwork_pending, shipping_unresolvable, a launch gate).
            // Do NOT prepend a domain-specific prefix here -- it will be wrong
            // for any other reason and contradict the backend message.
            const messages = (launch.reasons || [])
              .map((b) => b.message)
              .filter(Boolean);
            const detail = messages.length
              ? messages.join(" ")
              : "This store cannot be activated. Check the Review & Launch page for details.";
            this.$store.dispatch("raiseError", { message: detail });
            return;
          }
        } catch (err) {
          // Non-fatal: if readiness can't be checked, fall through
          // and let the backend gate reject if needed (we'll show the
          // server error). This preserves the legacy behavior when
          // /readiness/ is unreachable.
          // eslint-disable-next-line no-console
          console.warn("StoreList readiness pre-flight failed", describeError(err));
        }

        let sameShopperUsernameCampaign = await this.$axios.get(
          `/campaign/checkExistShopperUsername/${campaign.id}`
        );
        // get activated campaign that has same slug
        // slug-unify Phase 4 PR-3 (#2286): read the canonical ``slug`` (the
        // list response no longer carries ``shopper_url``). The endpoint
        // matches its path segment against ``slug`` on the backend.
        let sameShopperURLCampaign;
        if (campaign.slug) {
          sameShopperURLCampaign = await this.$axios.get(
            `/campaign/checkExistShopperURL/${
              campaign.id
            }/${campaign.slug.toLowerCase()}`
          );
        }
        // if there is already an activated campaign that has the shopper account or custom url,
        // show popup dialog
        if (
          sameShopperUsernameCampaign.data ||
          (sameShopperURLCampaign && sameShopperURLCampaign.data)
        ) {
          campaign.active = false;

          this.showCredentialDialogDirectly = false;
          let res = sameShopperUsernameCampaign.data
            ? sameShopperUsernameCampaign.data
            : sameShopperURLCampaign.data;
          this.selectedStore = campaign;
          this.activatedCampaign = res;
          // slug-unify Phase 4 PR-3 (#2286): the collision-probe response
          // now carries ``slug`` (not ``shopper_url``). Guard the read so an
          // absent value can never crash the activate flow (repro predating
          // this: store 22/23 slug collision under impersonation).
          if (res.slug) {
            this.activatedCampaign.slug = res.slug.toLowerCase();
          }
          this.activatedCampaignShopperUsername = res.username;
          this.activatedCampaignShopperURL = res.slug
            ? res.slug.toLowerCase()
            : "";
          this.createdByAnotherSalesRep = false;
          this.disableDeactiveCampaignBy = "";

          // A Sales Executive may deactivate ONLY a store they created
          // (Jake, 2026-10-06); the server refuses the rest with a 404.
          // Decided on the role SLUG and created_by_id: role ids are
          // seed-order data (1..7 on sandboxes, 8..14 on production), and
          // the numeric comparison below (still the rule for other roles)
          // let an SE close a Builder's store while crashing on an Owner's.
          const account = this.$store.state.account;
          const currentRoleSlug = account?.role_slug ?? account?.role?.slug;
          if (currentRoleSlug === ROLE_SLUGS.SALES_EXECUTIVE) {
            // #2513: compare the ACCOUNT id (jsonapi-vuex `_jv.id`; the
            // localStorage fallback is written from `account_id` at every
            // login path), never the role id, against created_by_id.
            const currentUserId =
              account?._jv?.id || localStorage.getItem("userId");
            this.createdByAnotherSalesRep =
              String(currentUserId) !==
              String(this.activatedCampaign.created_by_id);
            this.duplicateCredentialDialog = true;
            return;
          }

          // Optional-chain the role/id accesses. Under SA-impersonation
          // state.account is null (the boot accounts/:id fetch 404s for
          // the SA's own row, so dispatch('login') never ran). The
          // `||` fallback to localStorage was always intended; the
          // optional chain lets us actually reach it instead of
          // throwing a TypeError that abandons the activate flow
          // and silently snaps the toggle back. Live repro: 2026-06-15,
          // impersonating ACME, toggling store 22 (RNR-TIRE) whose
          // shopper_url collides with store 23.
          const currentUserRoleId =
            this.$store.state.account?.role?.id || localStorage.getItem("roleId");
          let result = await this.$axios.get(
            `/account/getUserRoleIdById/${this.activatedCampaign.created_by_id}`
          );
          const activatedCampaignCreatorRoleId = parseInt(result.data);
          if (currentUserRoleId > activatedCampaignCreatorRoleId) {
            this.disableDeactiveCampaignBy = this.possibleRoles.find(
              (role) => role._jv.id == activatedCampaignCreatorRoleId
            ).name;
          } else {
            this.disableDeactiveCampaignBy = "";
          }

          this.duplicateCredentialDialog = true;

          return;
        }

        campaign.end_date = reopenedCloseDate(campaign);
      }
      campaign.is_manually_closed = !campaign.active;
      let result = await this.$axios.get(
        `/campaign/activeCampaign/${campaign.id}/${campaign.active}/${campaign.end_date}/${campaign.is_manually_closed}`
      );

      // preserveOrder=true: the default sort is modified_at desc, so
      // the toggled row would otherwise jump to position 0. Keep the
      // user's existing row positions; only the toggled cell flips.
      this.loadStores({ preserveOrder: true });
    },

    async changeCampaignActiveStatus(campaign) {
      if (campaign.active) {
        campaign.end_date = reopenedCloseDate(campaign);
      } else {
      }

      campaign.is_manually_closed = !campaign.active;
      let result = await this.$axios.get(
        `/campaign/activeCampaign/${campaign.id}/${campaign.active}/${campaign.end_date}/${campaign.is_manually_closed}`
      );
    },

    async openCredentialsDialog() {
      let result = await this.$axios.get(
        `/campaign/getShopperInfoFromCampaignId/${this.selectedStore.id}`
      );
      if (result.data != "") {
        this.newShopperLogin = result.data.username;
      } else {
        this.newShopperLogin = "";
      }

      // slug-unify Phase 4 PR-3 (#2286): read the canonical ``slug`` (the
      // list response no longer carries ``shopper_url``); guard so an absent
      // value cannot crash the change-credentials dialog.
      this.newShopperCustomLink = this.selectedStore.slug
        ? this.selectedStore.slug.toLowerCase()
        : "";
      this.newShopperPassword = "[PASSWORD SAVED]";
      this.duplicateCredentialDialog = false;
      this.changeCredentialDialog = true;

      // Determine which fields to show based on conflicts
      this.displayChangeURLField = !!(
        this.newShopperCustomLink &&
        this.activatedCampaignShopperURL == this.newShopperCustomLink
      );
      this.displayChangeCredentialField =
        this.activatedCampaignShopperUsername == this.newShopperLogin;
    },

    async deactivateCampaign() {
      // Deactivate original campaign and activate duplicated campaign
      this.activatedCampaign.active = false;
      await this.changeCampaignActiveStatus(this.activatedCampaign);
      this.selectedStore.active = true;
      await this.changeCampaignActiveStatus(this.selectedStore);
      this.duplicateCredentialDialog = false;
      this.loadStores();
    },

    async changeCredentials({ login, password, customLink }) {
      if (login === "") {
        throw "Account username cannot be blank";
      }
      if (password !== null && password === "") {
        throw "Account password cannot be blank";
      }

      // ``store_id`` is the self-exclusion hint (GH #1861): this dialog is
      // pre-populated by ``openCredentialsDialog`` with the store's OWN
      // username and slug, and there is no dirty check here -- we re-probe
      // on every submit, including submits that changed only the password.
      // Without this param the platform-wide uniqueness COUNT matches the
      // store being edited and reports it "taken" against itself, leaving
      // the dialog unsubmittable with no field the user can change to
      // escape. The backend treats the param as optional and constrains it
      // to the caller's own tenant, so it excludes exactly this store and
      // nothing else -- a sibling or another tenant's store holding the
      // value is still a genuine conflict.
      const selfExclusion = `?store_id=${this.selectedStore.id}`;

      let res = await this.$axios.get(
        `/campaign/checkActivatedCampaignByShopper/${login}/${this.selectedStore.distributor_id}${selfExclusion}`
      );
      // if an user inputs an username that is already used in the other activated campaign
      // or campaign under different distributor, show notification.
      if (res.data != "") {
        this.$store.dispatch("raiseError", "Username already exists!!");
        return;
      }
      if (customLink) {
        res = await this.$axios.get(
          `/campaign/checkActivatedCampaignByShopperURL/${customLink}/${this.selectedStore.distributor_id}${selfExclusion}`
        );
        // if an user inputs a shopper_url that is already used in the other activated campaign
        // or campaign under different distributor, show notification.
        if (res.data != "") {
          this.$store.dispatch("raiseError", "Shopper URL already exists");
          return;
        }
      }

      let result;
      try {
        result = await this.$axios.post("/campaign/updateShopper", {
          username: login,
          password: password,
          distributor_id: this.selectedStore.distributor_id,
          campaign_id: this.selectedStore.id,
        });
      } catch (err) {
        this.$store.dispatch('raiseError', err);
        // NOT a `finally` any more (#3252). The guard below reads
        // `result.data.error`, and on a throw `result` is undefined --
        // so the guard silently skipped and execution FELL THROUGH to
        // the URL change below, activating the store on the back of a
        // credentials update that had already failed.
        return;
      }

      if (result && result.data && result.data.error) {
        this.$store.dispatch("raiseError", result.data.error);
        return;
      }

      try {
        let result = await this.$axios.post(
          `/stores/${this.selectedStore.id}/changeCampaignShopperURL`,
          {
            shopperLogin: login,
            shopperPassword: password,
            shopper_url: customLink.toLowerCase(),
          }
        );
      } catch (err) {
        this.$store.dispatch("raiseError", err);
        return;
      }
      this.changeCredentialDialog = false;
      this.selectedStore.active = true;
      await this.changeCampaignActiveStatus(this.selectedStore);
      this.loadStores();
    },

    async changeCompletenessHandler(campaign) {
      campaign.loading = true;
      this.completeButtonKey++;
      if (campaign.complete) {
        this.openUncompleteDialog(campaign);
      } else {
        await this.getCampaignHasPendingOrders(campaign);
        console.log(campaign.has_pending_orders);
        if (campaign.has_pending_orders) {
          this.openBatchAndCompleteDialog(campaign);
        } else {
          this.openCompleteDialog(campaign);
        }
      }
      campaign.loading = false;
      this.completeButtonKey++;
    },

    async getCampaignHasPendingOrders(campaign) {
      let result;
      try {
        result = await this.$axios.get(
          `/batch/getCampaignHasPendingOrders/${campaign.id}`
        );
      } catch (error) {
        this.$store.dispatch('raiseError', error);
      }
      campaign.has_pending_orders = result.data;
    },

    openBatchAndCompleteDialog(campaign) {
      this.completeCampaignData = campaign;
      console.log("openBatchAndCompleteDialog");
      this.$refs.createBatchDialog.open();
    },

    async openCompleteDialog(campaign) {
      campaign.loading = true;
      this.completeButtonKey++;

      this.completeCampaignData = campaign;
      this.completeCampaignDialogOpened = true;
    },

    closeCompleteDialog() {
      this.completeCampaignData = null;
      this.completeCampaignDialogOpened = false;
    },

    async processOrdersAndCompleteStore(distributorPO) {
      await this.processOrders(distributorPO);
      await this.completeCampaign(this.completeCampaignData, distributorPO);
    },

    async processOrders(distributorPO) {
      this.itemsPending = true;
      let batchName = this.$refs["createBatchDialog"].batchName;

      try {
        await this.$axios.post("batch/create", {
          orders: null,
          batch_name: batchName,
          campaign_id: this.completeCampaignData.id,
          distributor_po: distributorPO,
          process_all_orders: true,
        });
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      }
    },

    async completeCampaign(campaign, distributorPO) {
      this.completeCampaignPending = true;
      this.storesPending = true;
      try {
        campaign.complete = true;
        campaign.distributor_po = distributorPO;
        if (campaign.active) {
          campaign.end_date = reopenedCloseDate(campaign);
        }
        this.closeCompleteDialog();
        await this.$axios.get(
          `/campaign/setLockStatusCampaign/${campaign.id}/${campaign.complete}/${campaign.distributor_po}/${campaign.end_date}`
        );
        await this.$axios.get(
          `/campaign/sendEmailAfterLockCampaign/${campaign.id}`
        );
        this.loadStores();
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      } finally {
        this.completeCampaignPending = false;
        this.storesPending = false;
      }
    },

    openUncompleteDialog(campaign) {
      this.uncompleteCampaignData = campaign;
      this.uncompleteCampaignDialogOpened = true;
    },

    closeUncompleteDialog() {
      this.uncompleteCampaignData = null;
      this.uncompleteCampaignDialogOpened = false;
    },

    async uncompleteCampaign(campaign) {
      this.uncompleteCampaignPending = true;
      this.storesPending = true;
      campaign.complete = false;

      try {
        if (campaign.active) {
          campaign.end_date = reopenedCloseDate(campaign);
        }
        let result = await this.$axios.get(
          `/campaign/setLockStatusCampaign/${campaign.id}/${campaign.complete}/${campaign.distributor_po}/${campaign.end_date}`
        );
        this.closeUncompleteDialog();
        this.loadStores();
      } finally {
        this.uncompleteCampaignPending = false;
        this.storesPending = false;
      }
    },
  },
};
</script>

<style scoped>
.headline {
  font-size: 30px !important;
}
.actions {
  white-space: nowrap;
}

.add-campaign-button {
  margin-left: 8px;
  min-width: 140px;
}

.actions .v-btn {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.v-datatable.v-table th {
  font-size: 16px !important;
}

.v-table__overflow tbody td {
  font-size: 15px !important;
}
.v-datatable__actions {
  border-top: none !important;
}
</style>
<style>
.none-collapse-table > .v-table__overflow > .v-table {
  border-collapse: unset !important;
}

.none-collapse-table > .v-table__overflow > .v-table th {
  border-bottom: 1px solid grey;
}

.v-input--switch .text-accent {
  color: rgba(0, 0, 0, 0.87) !important;
  caret-color: rgba(0, 0, 0, 0.87) !important;
  /* color: var(--tf-primary) !important; */
}

.theme--light.v-input--switch__thumb {
  color: black;
}

.rows-count {
  max-width: 55px;
  margin: 10px 20px 0 20px;
}

.section-pagination {
  align-items: center;
  font-size: 12px;
}

.text-pagination {
  color: rgba(0, 0, 0, 0.54);
}
.theme--light.v-pagination .v-pagination__item--active {
  background-color: rgba(0, 0, 0, 0.12) !important;
  color: var(--tf-primary);
}
.table-footer {
  display: flex;
  justify-content: flex-end;
}
.link {
  text-decoration: none;
}

.v-datatable__progress {
  display: none;
}

.filler-column {
  width: 50px;
}

.list-title {
  margin-top: 25px;
  margin-left: 15px;
}

.search-filter {
  min-width: 180px;
  max-width: 220px;
}

.campaign-status {
  margin-top: 8px;
  color: #111;
  font-size: 14px;
}

.campaign-status-error {
  color: #d32f2f;
  font-weight: 500;
}

.none-collapse-table > .v-table__overflow > .v-table th {
  border-bottom: 0px !important;
}

.store-name {
  font-weight: 500 !important;
}

.campaign-list-page {
  width: 100%;
}

.campaign-toolbar {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.campaign-filter-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  padding: 12px 16px;
  background: var(--tf-bg);
  border: 1px solid var(--tf-border);
  border-radius: 12px;
}

.campaign-filter-bar__status-pills {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.campaign-filter-pill {
  display: inline-block;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 400;
  color: var(--tf-fg-3);
  background: #fafbfc;
  border: 1px solid var(--tf-border);
  border-radius: 9999px;
  cursor: pointer;
  user-select: none;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}
.campaign-filter-pill:hover {
  background: #f1f5f9;
}
.campaign-filter-pill:focus-visible {
  outline: 2px solid var(--tf-primary);
  outline-offset: 1px;
}
.campaign-filter-pill--active {
  background: var(--tf-primary);
  color: var(--tf-bg);
  border-color: var(--tf-primary);
  font-weight: 500;
}

.campaign-filter-bar__status {
  margin-left: auto;
  color: var(--tf-fg-3);
  font-size: 12px;
}

.campaign-table-wrapper {
  width: 100%;
  margin-top: 16px;
  max-height: calc(100vh - 210px);
  overflow: auto;
}

.campaign-list {
  background: var(--tf-bg);
}

.campaign-list thead th {
  background: var(--tf-bg);
}

.campaign-list .table-header-row th {
  position: sticky;
  top: 0;
  z-index: 6;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.2px;
  text-transform: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.campaign-list .table-filter-row th {
  position: sticky;
  top: 48px;
  z-index: 5;
  background: var(--tf-surface-muted) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.campaign-list .table-header-row th .d-flex {
  align-items: center;
}

#table-header .is-sortable {
  user-select: none;
}

.sort-icon {
  margin-left: 6px;
  opacity: 0.65;
  font-size: 18px !important;
}

.sort-icon-active {
  opacity: 1;
}

.campaign-list td,
.campaign-list th,
.campaign-list p,
.campaign-list span,
.campaign-list a {
  color: #111 !important;
}

.campaign-list tbody tr {
  height: 64px;
}

.campaign-list tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.campaign-clickable-row {
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
}

.campaign-clickable-row:hover {
  background-color: rgba(239, 4, 5, 0.04) !important;
}

.sales-rep-cell {
  min-width: 220px;
  width: 260px;
}

@media (max-width: 960px) {
  .campaign-toolbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .campaign-status {
    font-size: 13px;
  }

  .campaign-table-wrapper {
    max-height: calc(100vh - 240px);
  }

  .filler-column {
    width: 12px;
  }

  .campaign-list .table-header-row th {
    font-size: 13px;
    padding: 10px 8px;
  }

  .campaign-list .table-filter-row th {
    top: 44px;
    padding: 6px 8px;
  }

  .campaign-list td,
  .campaign-list th {
    padding-left: 8px;
    padding-right: 8px;
  }

  /* Allow store names and other text to wrap instead of truncating (#289) */
  .campaign-list td {
    white-space: normal;
    word-break: break-word;
  }

  .search-filter {
    min-width: 130px;
    max-width: 150px;
  }

  .add-campaign-button {
    min-width: 120px;
  }
}
</style>
